import { createContext, createElement, ReactNode, useCallback, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Base64 } from "js-base64";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import { getTouchpointVersion } from "generated/api/touchpoint-version";
import type {
  CreateEmailTouchpointWithContentRequest,
  LinkDTO,
  TouchpointVersionCloneRequest,
  TouchpointVersionCreateRequest,
  TouchpointVersionDTOAiFeedbackRating,
  TouchpointVersionPatchRequest,
  TouchpointVersionPatchRequestStatus,
} from "generated/schemas";

import { mapTouchpointVersionDTO, normalizeTouchpointVersionListResponse } from "utilities/api-mappers";

import type { BeeContent, TouchpointBuilderContent } from "types";
import { PaginatedRequestOptions, PaginatedResponse } from "types/pagination";
import { TouchpointType, type LpRedirectionType } from "types/touchpoint";
import { isEmail, usesLandingPageBuilder } from "types/typeguards";
import type { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

export interface LockedBy {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Aggregate view used across the touchpoint builder UI. Carries the union of
 * fields produced by Orval's `TouchpointVersionSummaryDTO` (list) and
 * `TouchpointVersionDTO` (detail, a superset), plus a handful of joined fields
 * the backend returns on list / detail responses but does not model as a single
 * DTO (e.g. `touchpointName`, `campaignName`, `openCommentCount`).
 * `mapTouchpointVersionDTO` is the source of truth.
 *
 * Content fields (`data`, `preprocessedHtml`, `finalHtml`, `advanced*Snippet`)
 * only exist on the detail payload — they are absent on list items.
 */
export interface TouchpointVersionAttributes {
  advancedBodySnippet?: string;
  advancedHeadSnippet?: string;
  applyClientTheme?: boolean;
  data?: BeeContent;
  finalHtml?: string;
  id: string;
  publishable?: boolean;
  name?: string;
  lastModifiedDate?: string;
  linkParams?: string;
  lockedBy?: LockedBy | null;
  preprocessedHtml?: string;
  sourceType?: string;
  status?: string;
  themeId?: string;
  touchpointId: string;
  touchpointName?: string;
  touchpointBatchId?: string;
  campaignName?: string;
  campaignId?: string;
  addedToBatchDate?: string;
  openCommentCount?: number;
  type?: TouchpointType;
  version?: number;
  description?: string;
  rewriterChanges?: string;
  isSummaryLoading?: boolean;
  aiGenerated?: boolean;
  aiFeedbackRating?: TouchpointVersionDTOAiFeedbackRating;
  fiscalYear?: number;
}

/**
 * N.B. If you add a new field that can be updated via the email builder, you
 * MUST add it to the `isEqualEmailFormPartial` util function — otherwise that
 * field change won't trigger autosave.
 */
export interface EmailAttributes extends TouchpointVersionAttributes {
  senderName?: string;
  senders?: ISenderDetails[];
  iterableTemplateId?: number;
  iterableChildTemplateIds?: string[];
  iterableEmailTypeId?: string;
  iterableEmailTypeName?: string;
  iterableEmailSenderId?: string;
  preheader?: string;
  replyToEmail?: string;
  senderEmail?: string;
  subjectLine?: string;
  emptyContent?: boolean;
}

/**
 * N.B. If you add a new field that can be updated via the landing page builder,
 * you MUST add it to the `isEqualLPFormPartial` util function — otherwise that
 * field change won't trigger autosave.
 */
export interface LandingPageAttributes extends TouchpointVersionAttributes {
  pageTitle?: string;
  pageDescription?: string;
  url?: string;
  redirectDestination?: string;
  redirectDestinationType?: LpRedirectionType;
  disablePrefill?: boolean;
}

export type TouchpointIds = {
  campaignId: string;
  clientId: string;
  id: string;
  touchpointId: string;
};

export type ThemeIds = {
  clientId: string;
  id: string;
};

export type TouchpointVersionsParams = PaginatedRequestOptions & {
  clientId?: string;
  campaignId?: string;
  touchpointId?: string;
};

type TouchpointVersionScope = {
  clientId?: string;
  campaignId?: string;
  touchpointId?: string;
};

type TouchpointVersionDetailParams = TouchpointVersionScope & {
  id?: string;
};

const touchpointVersionApi = getTouchpointVersion();

/**
 * Build Orval Pageable params from legacy `PaginatedRequestOptions`.
 */
function buildVersionListPageable(options: PaginatedRequestOptions) {
  const pageable: Record<string, unknown> = {};
  if (options.page !== undefined) pageable.page = options.page;
  if (options.size !== undefined) pageable.size = options.size;
  if (options.sort) pageable.sort = [options.sort];
  return pageable as { page?: number; size?: number; sort?: string[] };
}

/**
 * WAF workaround inherited from the legacy `TouchpointVersion` model: long /
 * html-bearing fields are Base64-encoded before PUT/PATCH so they don't trip
 * firewall rules. The backend decodes them on receipt. PATCH and PUT both go
 * through this; the backend tolerates non-Base64 values.
 *
 * See AGENTS.md "Known Pitfalls" for context.
 */
const VERSION_FIELDS_TO_ENCODE = [
  "advancedBodySnippet",
  "advancedHeadSnippet",
  "finalHtml",
  "preprocessedHtml",
  "subjectLine",
  "preheader",
  "url",
  "pageTitle",
  "pageDescription",
  "redirectDestination",
] as const;

function encodeVersionPayload<T extends Record<string, unknown>>(payload: T): T {
  const encoded: Record<string, unknown> = JSON.parse(JSON.stringify(payload));

  if (encoded.data !== undefined && encoded.data !== null) {
    encoded.data = Base64.encode(JSON.stringify(encoded.data));
  }

  for (const field of VERSION_FIELDS_TO_ENCODE) {
    const value = encoded[field];
    if (typeof value === "string" && value.length > 0) {
      encoded[field] = Base64.encode(value);
    }
  }

  return encoded as T;
}

const BuilderContentContext = createContext<TouchpointVersionAttributes | undefined>(undefined);

export const BuilderContentProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: TouchpointVersionAttributes;
}) => createElement(BuilderContentContext.Provider, { value }, children);

export const useTouchpointVersions = <TData = PaginatedResponse<TouchpointVersionAttributes>>(
  params: TouchpointVersionsParams,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<TouchpointVersionAttributes>, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { clientId, campaignId, touchpointId, ...listParams } = params;

  return useQuery({
    queryKey: touchpointVersionKeys.list(
      clientId ?? "",
      campaignId ?? "",
      touchpointId ?? "",
      listParams,
    ),
    queryFn: async () => {
      if (!clientId || !campaignId || !touchpointId) {
        throw new Error("Context required for useTouchpointVersions queryFn");
      }

      const response = await touchpointVersionApi.getAllVersions(
        { clientId, campaignId, touchpointId },
        buildVersionListPageable(listParams),
      );

      return normalizeTouchpointVersionListResponse(response);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && Boolean(touchpointId) && (options?.enabled ?? true),
  });
};

export const useTouchpointVersion = <TData = TouchpointVersionAttributes>(
  params: TouchpointVersionDetailParams,
  options?: Omit<UseQueryOptions<TouchpointVersionAttributes, Error, TData>, "queryKey" | "queryFn">,
) => {
  const { clientId, campaignId, touchpointId, id } = params;

  return useQuery({
    queryKey: touchpointVersionKeys.detail(
      clientId ?? "",
      campaignId ?? "",
      touchpointId ?? "",
      id ?? "",
    ),
    queryFn: async () => {
      if (!clientId || !campaignId || !touchpointId || !id) {
        throw new Error("Context required for useTouchpointVersion queryFn");
      }

      const dto = await touchpointVersionApi.getOneVersion({
        clientId,
        campaignId,
        touchpointId,
        id,
      });

      return mapTouchpointVersionDTO(dto);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && Boolean(touchpointId) && Boolean(id) && (options?.enabled ?? true),
  });
};

export function createTouchpointBuilderContent(
  attributes: TouchpointVersionAttributes | null | undefined,
): TouchpointBuilderContent | null {
  if (isEmail(attributes)) {
    return attributes as EmailAttributes;
  }
  if (usesLandingPageBuilder(attributes)) {
    return attributes as LandingPageAttributes;
  }
  return null;
}

export function useBuilderContent(): TouchpointBuilderContent {
  const providedBuilderContent = useContext(BuilderContentContext);
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const { versionId } = useParams<{ versionId: string }>();
  const { data } = useTouchpointVersion({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
    id: versionId,
  }, {
    enabled: !providedBuilderContent,
  });
  const builderData = providedBuilderContent ?? data;
  const content = useMemo(() => createTouchpointBuilderContent(builderData), [builderData]);

  if (!content) {
    throw new Error("useBuilderContent: called before content was cached");
  }

  return content;
}

export function setTouchpointVersionCache(
  queryClient: QueryClient,
  scope: Required<TouchpointVersionScope>,
  updatedVersion: TouchpointVersionAttributes,
) {
  queryClient.setQueryData(
    touchpointVersionKeys.detail(
      scope.clientId,
      scope.campaignId,
      scope.touchpointId,
      updatedVersion.id,
    ),
    updatedVersion,
  );

  queryClient.setQueriesData<PaginatedResponse<TouchpointVersionAttributes>>(
    {
      queryKey: touchpointVersionKeys.listScope(
        scope.clientId,
        scope.campaignId,
        scope.touchpointId,
      ),
    },
    (currentData) => {
      if (!currentData) return currentData;

      return {
        ...currentData,
        items: currentData.items.map((version) =>
          version.id === updatedVersion.id ? updatedVersion : version,
        ),
      };
    },
  );
}

export function setTouchpointVersionsCache(
  queryClient: QueryClient,
  scope: Required<TouchpointVersionScope>,
  data: PaginatedResponse<TouchpointVersionAttributes>,
  params?: PaginatedRequestOptions,
) {
  queryClient.setQueryData(
    touchpointVersionKeys.list(scope.clientId, scope.campaignId, scope.touchpointId, params),
    data,
  );
}

export const useSetTouchpointVersionCache = (scope: TouchpointVersionScope) => {
  const queryClient = useQueryClient();
  const { clientId, campaignId, touchpointId } = scope;

  return useCallback(
    (updatedVersion: TouchpointVersionAttributes) => {
      if (!clientId || !campaignId || !touchpointId) return;

      setTouchpointVersionCache(
        queryClient,
        {
          clientId,
          campaignId,
          touchpointId,
        },
        updatedVersion,
      );
    },
    [queryClient, clientId, campaignId, touchpointId],
  );
};

export const useInvalidateTouchpointVersions = (scope?: TouchpointVersionScope) => {
  const queryClient = useQueryClient();
  const clientId = scope?.clientId;
  const campaignId = scope?.campaignId;
  const touchpointId = scope?.touchpointId;

  return useCallback(() => {
    if (clientId && campaignId && touchpointId) {
      return queryClient.invalidateQueries({
        queryKey: touchpointVersionKeys.listScope(
          clientId,
          campaignId,
          touchpointId,
        ),
      });
    }

    return queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
  }, [queryClient, clientId, campaignId, touchpointId]);
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

type VersionMutationScope = Required<TouchpointVersionScope>;

/**
 * Creating, publishing, archiving or deleting a version also moves fields the *touchpoint* owns —
 * `status`, `publishedVersion`, `latestVersion`, `validContentType`. Nothing here writes those, so the
 * touchpoint detail has to be invalidated too; until `staleTime` became non-zero, refetch-on-mount hid
 * that gap. Deliberately not wired into `useReplaceTouchpointVersion` / `useEditTouchpointVersion`:
 * those are the builder's autosave path and would add a touchpoint GET per save.
 */
function invalidateVersionScope(
  queryClient: QueryClient,
  scope: VersionMutationScope,
) {
  queryClient.invalidateQueries({
    queryKey: touchpointVersionKeys.listScope(
      scope.clientId,
      scope.campaignId,
      scope.touchpointId,
    ),
  });

  queryClient.invalidateQueries({
    queryKey: touchpointKeys.detail(scope.clientId, scope.campaignId, scope.touchpointId),
  });
  queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
}

/**
 * Create a new touchpoint version (POST).
 */
export const useCreateTouchpointVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TouchpointVersionCreateRequest) =>
      touchpointVersionApi.createTouchpointVersion(
        { clientId: scope.clientId, campaignId: scope.campaignId, touchpointId: scope.touchpointId },
        data,
      ),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
      invalidateVersionScope(queryClient, scope);
    },
  });
};

export type ReplaceTouchpointVersionVariables = {
  id: string;
  attributes: Partial<TouchpointVersionAttributes>;
};

/**
 * Full-replace a touchpoint version (PUT). Payload is Base64-encoded per the
 * legacy WAF workaround (see `encodeVersionPayload`).
 */
export const useReplaceTouchpointVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, attributes }: ReplaceTouchpointVersionVariables) =>
      touchpointVersionApi.updateTouchpointVersion(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          id,
        },
        encodeVersionPayload(attributes as unknown as Record<string, unknown>) as never,
      ),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
    },
  });
};

export type EditTouchpointVersionVariables = {
  id: string;
  attributes: TouchpointVersionPatchRequest & Record<string, unknown>;
};

/**
 * Partial-update a touchpoint version (PATCH). Same Base64 workaround as PUT.
 */
export const useEditTouchpointVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, attributes }: EditTouchpointVersionVariables) =>
      touchpointVersionApi.patchTouchpointVersion(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          id,
        },
        encodeVersionPayload(attributes) as TouchpointVersionPatchRequest,
      ),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
    },
  });
};

export type PatchTouchpointVersionStatusVariables = {
  id: string;
  status: TouchpointVersionPatchRequestStatus;
};

/**
 * Narrow PATCH wrapper for status-only updates (publish, archive, etc).
 * Bypasses Base64 encoding because no html / large strings are sent.
 */
export const usePatchTouchpointVersionStatus = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: PatchTouchpointVersionStatusVariables) =>
      touchpointVersionApi.patchTouchpointVersion(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          id,
        },
        { status },
      ),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
      invalidateVersionScope(queryClient, scope);
    },
  });
};

export const useDeleteTouchpointVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      touchpointVersionApi.deleteTouchpointVersion({
        clientId: scope.clientId,
        campaignId: scope.campaignId,
        touchpointId: scope.touchpointId,
        id,
      }),
    onSuccess: () => {
      invalidateVersionScope(queryClient, scope);
    },
  });
};

export const useClaimVersionLock = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      touchpointVersionApi.claimLock({
        clientId: scope.clientId,
        campaignId: scope.campaignId,
        touchpointId: scope.touchpointId,
        id,
      }),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
    },
    onError: (error) => {
      console.error("Failed to claim touchpoint version lock", error);
    },
  });
};

export const useReleaseVersionLock = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      touchpointVersionApi.releaseLock({
        clientId: scope.clientId,
        campaignId: scope.campaignId,
        touchpointId: scope.touchpointId,
        id,
      }),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
    },
    onError: (error) => {
      console.error("Failed to release touchpoint version lock", error);
    },
  });
};

export type CreateDraftVersionVariables = {
  id: string;
  data: TouchpointVersionCreateRequest;
};

export const useCreateDraftVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: CreateDraftVersionVariables) =>
      touchpointVersionApi.createNewDraftVersion(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          id,
        },
        data,
      ),
    onSuccess: (dto) => {
      const attributes = mapTouchpointVersionDTO(dto);
      setTouchpointVersionCache(queryClient, scope, attributes);
      invalidateVersionScope(queryClient, scope);
    },
  });
};

export type CloneTouchpointVersionVariables = {
  id: string;
  data: TouchpointVersionCloneRequest;
};

/**
 * Clone a touchpoint version. The resulting version may belong to a different
 * touchpoint and/or campaign, so we invalidate the broader touchpoint trees
 * instead of trying to surgically update caches.
 */
export const useCloneTouchpointVersion = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: CloneTouchpointVersionVariables) =>
      touchpointVersionApi.cloneTouchpoint(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          id,
        },
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
    },
  });
};

export type CreateParentEmailVariables = {
  versionId: string;
  data: CreateEmailTouchpointWithContentRequest;
};

/**
 * AI parent-email creation: combined touchpoint + version create on top of an
 * existing student-version (used by the create-parent-touchpoint modal).
 */
export const useCreateParentEmail = (scope: VersionMutationScope) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ versionId, data }: CreateParentEmailVariables) =>
      touchpointVersionApi.createParentEmail(
        {
          clientId: scope.clientId,
          campaignId: scope.campaignId,
          touchpointId: scope.touchpointId,
          versionId,
        },
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
    },
  });
};

export type ExtractLinksScope = VersionMutationScope & { versionId: string };

/**
 * Fetch the list of links extracted from a touchpoint version's final HTML.
 */
export const useExtractLinksFromFinalHtml = (
  scope: ExtractLinksScope,
  options?: Omit<UseQueryOptions<LinkDTO[], Error, LinkDTO[]>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryKey: [
      ...touchpointVersionKeys.detail(
        scope.clientId,
        scope.campaignId,
        scope.touchpointId,
        scope.versionId,
      ),
      "links",
    ] as const,
    queryFn: () =>
      touchpointVersionApi.extractLinksFromFinalHtml({
        clientId: scope.clientId,
        campaignId: scope.campaignId,
        touchpointId: scope.touchpointId,
        versionId: scope.versionId,
      }),
    ...options,
    enabled:
      Boolean(scope.clientId) &&
      Boolean(scope.campaignId) &&
      Boolean(scope.touchpointId) &&
      Boolean(scope.versionId) &&
      (options?.enabled ?? true),
  });
};
